import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ECG_INITIAL_HISTORY_MINUTES,
  REGIONAL_MAX_HISTORY_MINUTES,
  REGIONAL_MAX_RETENTION_MS,
} from "../constants/regions";
import type { RegionalMeterPoint, RegionalMeterSample, RegionalMeterStore } from "../types/regional";
import { appendMeterSample, trimMeterSamples } from "../utils/regionalSeries";

type MainFreqSample = { ts_ms: number; freq_hz: number };

type EcgDataContextValue = {
  store: RegionalMeterStore;
  mainSamples: RegionalMeterPoint[];
  regionalConnected: boolean;
  mainConnected: boolean;
  historyLoading: boolean;
  error: string | null;
  loadedHistoryMinutes: number;
  registerHistoryMinutes: (minutes: number) => void;
  refreshHistory: () => void;
};

const EcgDataContext = createContext<EcgDataContextValue | null>(null);

function ingestSample(
  store: RegionalMeterStore,
  sample: RegionalMeterSample,
): RegionalMeterStore {
  const next: RegionalMeterStore = new Map(store);
  const regionMap = new Map(next.get(sample.region_id) ?? []);
  const prev = regionMap.get(sample.meter_id) ?? [];
  regionMap.set(
    sample.meter_id,
    appendMeterSample(prev, { ts_ms: sample.ts_ms, freq_hz: sample.freq_hz }),
  );
  next.set(sample.region_id, regionMap);
  return next;
}

function trimStore(store: RegionalMeterStore, nowMs: number): RegionalMeterStore {
  const cutoff = nowMs - REGIONAL_MAX_RETENTION_MS;
  const next: RegionalMeterStore = new Map();
  for (const [regionId, meters] of store) {
    const regionMap = new Map<string, RegionalMeterPoint[]>();
    for (const [meterId, samples] of meters) {
      const trimmed = trimMeterSamples(samples, cutoff);
      if (trimmed.length) regionMap.set(meterId, trimmed);
    }
    if (regionMap.size) next.set(regionId, regionMap);
  }
  return next;
}

function hydrateRegionalHistory(
  store: RegionalMeterStore,
  points: RegionalMeterSample[],
): RegionalMeterStore {
  let next = store;
  for (const p of points) {
    next = trimStore(ingestSample(next, p), p.ts_ms);
  }
  return next;
}

function hydrateMainHistory(
  samples: RegionalMeterPoint[],
  points: MainFreqSample[],
): RegionalMeterPoint[] {
  let next = samples;
  for (const p of points) {
    next = appendMeterSample(next, { ts_ms: p.ts_ms, freq_hz: p.freq_hz });
    next = trimMeterSamples(next, p.ts_ms - REGIONAL_MAX_RETENTION_MS);
  }
  return next;
}

export function EcgDataProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<RegionalMeterStore>(() => new Map());
  const [mainSamples, setMainSamples] = useState<RegionalMeterPoint[]>([]);
  const [regionalConnected, setRegionalConnected] = useState(false);
  const [mainConnected, setMainConnected] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedHistoryMinutes, setLoadedHistoryMinutes] = useState(0);
  const [requestedHistoryMinutes, setRequestedHistoryMinutes] = useState(
    ECG_INITIAL_HISTORY_MINUTES,
  );
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const loadedMinutesRef = useRef(0);
  const fetchGenRef = useRef(0);
  const lastFetchRefreshKeyRef = useRef(-1);

  const registerHistoryMinutes = useCallback((minutesRaw: number) => {
    const minutes = Math.max(
      1,
      Math.min(REGIONAL_MAX_HISTORY_MINUTES, Math.round(minutesRaw)),
    );
    setRequestedHistoryMinutes((prev) => Math.max(prev, minutes));
  }, []);

  const refreshHistory = useCallback(() => {
    setHistoryRefreshKey((k) => k + 1);
  }, []);

  const applyRegionalSample = useCallback((sample: RegionalMeterSample) => {
    setStore((prev) => trimStore(ingestSample(prev, sample), sample.ts_ms));
  }, []);

  const applyMainSample = useCallback((point: MainFreqSample) => {
    setMainSamples((prev) => {
      const next = appendMeterSample(prev, point);
      return trimMeterSamples(next, point.ts_ms - REGIONAL_MAX_RETENTION_MS);
    });
  }, []);

  useEffect(() => {
    const minutes = requestedHistoryMinutes;
    if (
      minutes <= loadedMinutesRef.current &&
      historyRefreshKey === lastFetchRefreshKeyRef.current
    ) {
      return;
    }

    const gen = ++fetchGenRef.current;
    lastFetchRefreshKeyRef.current = historyRefreshKey;
    setHistoryLoading(true);

    (async () => {
      try {
        const [regionalRes, mainRes] = await Promise.all([
          fetch(`/api/freq/regional/history?minutes=${minutes}`),
          fetch(`/api/freq/main/history?minutes=${minutes}`),
        ]);
        if (gen !== fetchGenRef.current) return;
        if (!regionalRes.ok) throw new Error(`regional history ${regionalRes.status}`);
        if (!mainRes.ok) throw new Error(`main history ${mainRes.status}`);

        const regionalBody = (await regionalRes.json()) as {
          points?: RegionalMeterSample[];
        };
        const mainBody = (await mainRes.json()) as { points?: MainFreqSample[] };
        if (gen !== fetchGenRef.current) return;

        const regionalPoints = regionalBody.points ?? [];
        const mainPoints = mainBody.points ?? [];
        if (regionalPoints.length) {
          setStore((prev) => hydrateRegionalHistory(prev, regionalPoints));
        }
        if (mainPoints.length) {
          setMainSamples((prev) => hydrateMainHistory(prev, mainPoints));
        }
        loadedMinutesRef.current = minutes;
        setLoadedHistoryMinutes(minutes);
        setError(null);
      } catch (err) {
        if (gen !== fetchGenRef.current) return;
        setError(err instanceof Error ? err.message : "Failed to load history");
      } finally {
        if (gen === fetchGenRef.current) setHistoryLoading(false);
      }
    })();
  }, [requestedHistoryMinutes, historyRefreshKey]);

  useEffect(() => {
    const es = new EventSource("/api/freq/regional/live");

    es.addEventListener("regional_meter", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as RegionalMeterSample;
        if (!data?.region_id || !data?.meter_id) return;
        applyRegionalSample(data);
        setRegionalConnected(true);
        setError(null);
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener("control", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as {
          type?: string;
          mqtt_connected?: boolean;
          error?: string;
        };
        if (data.type === "mqtt_closed") setRegionalConnected(false);
        if (data.mqtt_connected) setRegionalConnected(true);
        if (data.error) setError(data.error);
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      setRegionalConnected(false);
      if (loadedMinutesRef.current > 0) {
        setError((prev) => prev ?? "Regional meter stream disconnected");
      }
    };

    return () => es.close();
  }, [applyRegionalSample]);

  useEffect(() => {
    const es = new EventSource("/api/freq/live");

    es.addEventListener("sample", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as MainFreqSample;
        if (!Number.isFinite(data.ts_ms) || !Number.isFinite(data.freq_hz)) return;
        applyMainSample(data);
        setMainConnected(true);
        setError(null);
      } catch {
        /* ignore malformed */
      }
    });

    es.addEventListener("control", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as {
          type?: string;
          mqtt_connected?: boolean;
          error?: string;
        };
        if (data.type === "mqtt_closed") setMainConnected(false);
        if (data.mqtt_connected) setMainConnected(true);
        if (data.error) setError(data.error);
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      setMainConnected(false);
      if (loadedMinutesRef.current > 0) {
        setError((prev) => prev ?? "Main frequency stream disconnected");
      }
    };

    return () => es.close();
  }, [applyMainSample]);

  const value = useMemo(
    () => ({
      store,
      mainSamples,
      regionalConnected,
      mainConnected,
      historyLoading,
      error,
      loadedHistoryMinutes,
      registerHistoryMinutes,
      refreshHistory,
    }),
    [
      store,
      mainSamples,
      regionalConnected,
      mainConnected,
      historyLoading,
      error,
      loadedHistoryMinutes,
      registerHistoryMinutes,
      refreshHistory,
    ],
  );

  return <EcgDataContext.Provider value={value}>{children}</EcgDataContext.Provider>;
}

export function useEcgData(): EcgDataContextValue {
  const ctx = useContext(EcgDataContext);
  if (!ctx) {
    throw new Error("useEcgData must be used within EcgDataProvider");
  }
  return ctx;
}

/** Register visible chart range — provider fetches SQLite history up to the max requested. */
export function useEcgHistoryForRange(rangeMin: number) {
  const { registerHistoryMinutes } = useEcgData();
  useEffect(() => {
    registerHistoryMinutes(rangeMin);
  }, [rangeMin, registerHistoryMinutes]);
}
